import React, { useState, useEffect } from 'react';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Palette, Type, Building2 } from 'lucide-react';
import { indianStates, governmentHospitals, privateHospitals, privateClinics } from '../data/hospitalData';

const RealisticCustomization = ({ 
  customization, 
  onCustomizationChange, 
  templateType,
  onHospitalDataChange 
}) => {
  const [selectedState, setSelectedState] = useState('Maharashtra');
  const [hospitalList, setHospitalList] = useState([]);

  useEffect(() => {
    // Update hospital list based on template type and state
    if (templateType === 'govt-hospital') {
      setHospitalList(governmentHospitals[selectedState] || []);
    } else if (templateType === 'private-hospital') {
      setHospitalList(privateHospitals[selectedState] || []);
    } else if (templateType === 'private-clinic') {
      setHospitalList(privateClinics[selectedState] || []);
    }
  }, [selectedState, templateType]);

  const messyHandwrittenFonts = [
    { id: 'printed', name: 'Printed (Default)', family: 'system-ui' },
    { id: 'messy-1', name: 'Doctor Handwriting 1', family: 'Caveat' },
    { id: 'messy-2', name: 'Doctor Handwriting 2', family: 'Satisfy' },
    { id: 'messy-3', name: 'Messy Script', family: 'Dancing Script' },
    { id: 'messy-4', name: 'Rough Handwriting', family: 'Pacifico' },
    { id: 'messy-5', name: 'Quick Notes', family: 'Patrick Hand' }
  ];

  const penColors = [
    { id: 'black', name: 'Black Pen', color: '#000000' },
    { id: 'blue', name: 'Blue Pen', color: '#0000FF' },
    { id: 'dark-blue', name: 'Dark Blue', color: '#000080' },
    { id: 'red', name: 'Red Pen', color: '#DC2626' },
    { id: 'green', name: 'Green Pen', color: '#059669' }
  ];

  const mastheadColors = [
    { id: 'red', name: 'Medical Red', color: '#DC2626' },
    { id: 'blue', name: 'Professional Blue', color: '#2563EB' },
    { id: 'green', name: 'Health Green', color: '#059669' },
    { id: 'teal', name: 'Medical Teal', color: '#0D9488' },
    { id: 'purple', name: 'Royal Purple', color: '#7C3AED' },
    { id: 'maroon', name: 'Dark Maroon', color: '#7F1D1D' },
    { id: 'navy', name: 'Navy Blue', color: '#1E3A8A' },
    { id: 'orange', name: 'Orange', color: '#EA580C' }
  ];

  const handleHospitalSelect = (value) => {
    onHospitalDataChange({
      hospital: value,
      state: selectedState
    });
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">🎨 Customize Your Certificate</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Hospital/Clinic Selection for specific templates */}
        {(templateType === 'govt-hospital' || templateType === 'private-hospital' || templateType === 'private-clinic') && (
          <>
            <div>
              <Label htmlFor="state" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Select State
              </Label>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger id="state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {indianStates.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="hospital" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {templateType === 'govt-hospital' && 'Select Government Hospital'}
                {templateType === 'private-hospital' && 'Select Private Hospital'}
                {templateType === 'private-clinic' && 'Select Private Clinic'}
              </Label>
              <Select onValueChange={handleHospitalSelect}>
                <SelectTrigger id="hospital">
                  <SelectValue placeholder="Choose from list..." />
                </SelectTrigger>
                <SelectContent>
                  {hospitalList.map((hospital) => (
                    <SelectItem key={hospital} value={hospital}>{hospital}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Modern Template - Custom Clinic Name */}
        {templateType === 'modern' && (
          <>
            <div>
              <Label htmlFor="clinicName">Clinic/Hospital Name</Label>
              <Input
                id="clinicName"
                placeholder="Enter clinic name..."
                value={customization.clinicName || ''}
                onChange={(e) => onCustomizationChange('clinicName', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="clinicAddress">Clinic Address</Label>
              <Input
                id="clinicAddress"
                placeholder="Enter clinic address..."
                value={customization.clinicAddress || ''}
                onChange={(e) => onCustomizationChange('clinicAddress', e.target.value)}
              />
            </div>
          </>
        )}

        {/* Masthead Color for Modern template */}
        {templateType === 'modern' && (
          <div>
            <Label htmlFor="mastheadColor" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Masthead Color
            </Label>
            <Select 
              value={customization.mastheadColor} 
              onValueChange={(value) => onCustomizationChange('mastheadColor', value)}
            >
              <SelectTrigger id="mastheadColor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mastheadColors.map((color) => (
                  <SelectItem key={color.id} value={color.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded border"
                        style={{ backgroundColor: color.color }}
                      />
                      {color.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Color picker buttons */}
            <div className="flex gap-2 flex-wrap mt-2">
              {mastheadColors.map((color) => (
                <button
                  key={color.id}
                  onClick={() => onCustomizationChange('mastheadColor', color.id)}
                  className={`w-8 h-8 rounded border-2 transition-all ${
                    customization.mastheadColor === color.id ? 'ring-2 ring-offset-2 ring-pink-600 scale-110' : ''
                  }`}
                  style={{ backgroundColor: color.color }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        )}

        {/* Font Style Selection */}
        <div>
          <Label htmlFor="fontStyle" className="flex items-center gap-2">
            <Type className="h-4 w-4" />
            Handwriting Style
          </Label>
          <Select 
            value={customization.fontStyle} 
            onValueChange={(value) => onCustomizationChange('fontStyle', value)}
          >
            <SelectTrigger id="fontStyle">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {messyHandwrittenFonts.map((font) => (
                <SelectItem key={font.id} value={font.id}>
                  <span style={{ fontFamily: font.family }}>{font.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pen Color Selection */}
        {customization.fontStyle !== 'printed' && (
          <div>
            <Label htmlFor="penColor" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Pen Color
            </Label>
            <Select 
              value={customization.penColor || 'black'} 
              onValueChange={(value) => onCustomizationChange('penColor', value)}
            >
              <SelectTrigger id="penColor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {penColors.map((color) => (
                  <SelectItem key={color.id} value={color.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: color.color }}
                      />
                      {color.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Pen color picker buttons */}
            <div className="flex gap-2 flex-wrap mt-2">
              {penColors.map((color) => (
                <button
                  key={color.id}
                  onClick={() => onCustomizationChange('penColor', color.id)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    (customization.penColor || 'black') === color.id ? 'ring-2 ring-offset-2 ring-pink-600 scale-110' : ''
                  }`}
                  style={{ backgroundColor: color.color }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
};

export default RealisticCustomization;
